/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import type { ColumnDef } from '@tanstack/react-table'
import { Music } from 'lucide-react'
/* eslint-disable react-refresh/only-export-components */
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@/components/dialog'
import { StatusBadge } from '@/components/status-badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { getUserAvatarFallback, getUserAvatarStyle } from '@/lib/avatar'
import { formatLogQuota, formatTimestampToDate } from '@/lib/format'
import { cn } from '@/lib/utils'

import { TASK_ACTIONS, TASK_STATUS } from '../../constants'
import { taskActionMapper, taskStatusMapper } from '../../lib/mappers'
import type { TaskLog } from '../../types'
import {
  AudioPreviewDialog,
  type AudioClip,
} from '../dialogs/audio-preview-dialog'
import { FailReasonDialog } from '../dialogs/fail-reason-dialog'
import { useUsageLogsContext } from '../usage-logs-provider'
import {
  createDurationColumn,
  createChannelColumn,
  createProgressColumn,
} from './column-helpers'

function parseTaskData(data: unknown): unknown[] {
  if (Array.isArray(data)) return data
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

function getTaskModelName(log: TaskLog): string {
  if (log.model_name) return log.model_name
  if (log.properties && typeof log.properties === 'object') {
    const properties = log.properties as Record<string, unknown>
    const propertyModel = String(
      properties.origin_model_name || properties.upstream_model_name || ''
    )
    if (propertyModel) return propertyModel
  }
  if (log.data && typeof log.data === 'string') {
    try {
      const data = JSON.parse(log.data) as Record<string, unknown>
      if (typeof data.model === 'string') return data.model
    } catch {
      return ''
    }
  }
  if (log.data && typeof log.data === 'object' && !Array.isArray(log.data)) {
    const data = log.data as Record<string, unknown>
    if (typeof data.model === 'string') return data.model
  }
  return ''
}

function getTaskResultURL(log: TaskLog): string {
  const resultURL = log.result_url?.trim()
  if (resultURL && /^https?:\/\//i.test(resultURL)) return resultURL

  // Older successful video tasks stored their result URL in fail_reason.
  const legacyResultURL = log.fail_reason?.trim()
  if (
    log.status === TASK_STATUS.SUCCESS &&
    legacyResultURL &&
    /^https?:\/\//i.test(legacyResultURL)
  ) {
    return legacyResultURL
  }
  return ''
}

function TaskDetailsDialog({
  log,
  open,
  onOpenChange,
}: {
  log: TaskLog
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const details = useMemo(() => JSON.stringify(log, null, 2), [log])

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Task Details')}
      description={log.task_id}
      contentClassName='sm:max-w-3xl'
      contentHeight='60vh'
      bodyClassName='h-full'
    >
      <pre className='bg-muted/50 h-full overflow-auto rounded-md border p-3 font-mono text-xs leading-relaxed break-all whitespace-pre-wrap'>
        {details}
      </pre>
    </Dialog>
  )
}

function AudioPreviewCell({ log }: { log: TaskLog }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const clips = useMemo(() => {
    const data = parseTaskData(log.data)
    return data.filter(
      (c) =>
        c && typeof c === 'object' && (c as Record<string, unknown>).audio_url
    )
  }, [log.data])

  if (clips.length === 0) return null

  return (
    <>
      <button
        type='button'
        className='group flex items-center gap-1 text-left text-xs'
        onClick={() => setOpen(true)}
      >
        <Music className='text-muted-foreground size-3' />
        <span className='text-foreground leading-snug group-hover:underline'>
          {t('Click to preview audio')}
        </span>
      </button>
      <AudioPreviewDialog
        open={open}
        onOpenChange={setOpen}
        clips={clips as AudioClip[]}
      />
    </>
  )
}

export function useTaskLogsColumns(isAdmin: boolean): ColumnDef<TaskLog>[] {
  const { t } = useTranslation()
  const columns: ColumnDef<TaskLog>[] = [
    {
      accessorKey: 'submit_time',
      header: t('Submit Time'),
      cell: ({ row }) => {
        const log = row.original
        const submitTime = row.getValue('submit_time') as number

        return (
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span className='truncate font-mono text-xs tabular-nums'>
              {formatTimestampToDate(submitTime, 'seconds')}
            </span>
            {log.finish_time ? (
              <span className='text-muted-foreground/60 truncate font-mono text-[11px] tabular-nums'>
                {formatTimestampToDate(log.finish_time, 'seconds')}
              </span>
            ) : (
              <span className='text-muted-foreground/50 text-[11px]'>-</span>
            )}
          </div>
        )
      },
      size: 180,
    },
  ]

  if (isAdmin) {
    columns.push(createChannelColumn<TaskLog>({ headerLabel: t('Channel') }), {
      id: 'user',
      header: t('User'),
      accessorFn: (row) => row.username || row.user_id,
      cell: function UserCell({ row }) {
        const { sensitiveVisible, setSelectedUserId, setUserInfoDialogOpen } =
          useUsageLogsContext()
        const log = row.original
        const displayName = log.username || String(log.user_id || '?')

        return (
          <button
            type='button'
            className='flex items-center gap-1.5 text-left'
            onClick={(e) => {
              e.stopPropagation()
              setSelectedUserId(log.user_id)
              setUserInfoDialogOpen(true)
            }}
          >
            <Avatar className='ring-border/60 size-6 ring-1 max-sm:hidden'>
              <AvatarFallback
                className={cn(
                  'text-[11px] font-semibold',
                  !sensitiveVisible && 'bg-muted text-muted-foreground'
                )}
                style={
                  sensitiveVisible ? getUserAvatarStyle(displayName) : undefined
                }
              >
                {sensitiveVisible ? getUserAvatarFallback(displayName) : '•'}
              </AvatarFallback>
            </Avatar>
            <span className='text-muted-foreground truncate text-sm hover:underline'>
              {sensitiveVisible ? displayName : '••••'}
            </span>
          </button>
        )
      },
    })
  }

  columns.push(
    {
      accessorKey: 'task_id',
      header: t('Task ID'),
      cell: function TaskIdCell({ row }) {
        const log = row.original
        const taskId = row.getValue('task_id') as string
        const [dialogOpen, setDialogOpen] = useState(false)
        if (!taskId) {
          return <span className='text-muted-foreground/60 text-xs'>-</span>
        }
        return (
          <>
            <div className='flex max-w-[170px] flex-col gap-0.5'>
              <StatusBadge
                label={taskId}
                variant='neutral'
                size='sm'
                copyable={false}
                onClick={() => setDialogOpen(true)}
                title={t('Click to view full details')}
                className='border-border/60 bg-muted/30 !text-foreground hover:bg-muted max-w-full cursor-pointer truncate rounded-md border px-1.5 py-0.5 font-mono'
              />
              <span className='text-muted-foreground/60 truncate text-[11px]'>
                {t(log.platform)} · {t(taskActionMapper.getLabel(log.action))}
              </span>
            </div>
            <TaskDetailsDialog
              log={log}
              open={dialogOpen}
              onOpenChange={setDialogOpen}
            />
          </>
        )
      },
      meta: { mobileTitle: true },
    },
    {
      id: 'model_name',
      accessorFn: getTaskModelName,
      header: t('Model'),
      cell: ({ row }) => {
        const modelName = row.getValue('model_name') as string
        if (!modelName) {
          return <span className='text-muted-foreground/60 text-xs'>-</span>
        }
        return (
          <StatusBadge
            label={modelName}
            copyText={modelName}
            variant='neutral'
            size='sm'
            className='border-border/60 bg-muted/20 !text-foreground max-w-[180px] truncate rounded-md border px-1.5 py-0.5 font-mono'
          />
        )
      },
      size: 180,
    },
    {
      accessorKey: 'quota',
      header: t('Final Cost'),
      cell: function FinalCostCell({ row }) {
        const { sensitiveVisible } = useUsageLogsContext()
        const log = row.original
        if (log.status !== TASK_STATUS.SUCCESS) {
          if (log.status === TASK_STATUS.FAILURE) {
            return sensitiveVisible ? formatLogQuota(0) : '••••'
          }
          return <span className='text-muted-foreground/60 text-xs'>-</span>
        }
        return (
          <span className='font-mono text-xs tabular-nums'>
            {sensitiveVisible ? formatLogQuota(log.quota || 0) : '••••'}
          </span>
        )
      },
      size: 130,
    },
    createDurationColumn<TaskLog>({
      submitTimeKey: 'submit_time',
      finishTimeKey: 'finish_time',
      unit: 'seconds',
      headerLabel: t('Duration'),
      warningThresholdSec: 300,
    }),
    {
      accessorKey: 'status',
      header: t('Status'),
      cell: ({ row }) => {
        const status = row.getValue('status') as string
        return (
          <StatusBadge
            label={t(taskStatusMapper.getLabel(status, status || 'Submitting'))}
            variant={taskStatusMapper.getVariant(status)}
            size='sm'
            copyable={false}
            className='-ml-1.5'
          />
        )
      },
    },
    createProgressColumn<TaskLog>({ headerLabel: t('Progress') }),
    {
      id: 'result_url',
      accessorFn: getTaskResultURL,
      header: t('URL'),
      cell: ({ row }) => {
        const resultURL = row.getValue('result_url') as string
        if (!resultURL) {
          return <span className='text-muted-foreground/60 text-xs'>-</span>
        }

        return (
          <a
            href={resultURL}
            download
            className='text-primary text-xs hover:underline'
            title={t('Download')}
          >
            {t('Download')}
          </a>
        )
      },
      size: 80,
    },
    {
      accessorKey: 'fail_reason',
      header: t('Details'),
      cell: function DetailsCell({ row }) {
        const log = row.original
        const failReason = row.getValue('fail_reason') as string
        const status = log.status
        const [dialogOpen, setDialogOpen] = useState(false)

        const isSunoSuccess =
          log.platform === 'suno' && status === TASK_STATUS.SUCCESS
        if (isSunoSuccess) {
          const data = parseTaskData(log.data)
          if (
            data.some(
              (c) =>
                c &&
                typeof c === 'object' &&
                (c as Record<string, unknown>).audio_url
            )
          ) {
            return <AudioPreviewCell log={log} />
          }
        }

        const isVideoTask =
          log.action === TASK_ACTIONS.GENERATE ||
          log.action === TASK_ACTIONS.TEXT_GENERATE ||
          log.action === TASK_ACTIONS.FIRST_TAIL_GENERATE ||
          log.action === TASK_ACTIONS.REFERENCE_GENERATE ||
          log.action === TASK_ACTIONS.REMIX_GENERATE ||
          log.action === TASK_ACTIONS.VIDEO_EDIT
        const isSuccess = status === TASK_STATUS.SUCCESS
        const resultURL = getTaskResultURL(log)

        if (isSuccess && isVideoTask && resultURL) {
          const videoUrl = `/v1/videos/${log.task_id}/content`
          return (
            <a
              href={videoUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-foreground text-xs hover:underline'
            >
              {t('Click to preview video')}
            </a>
          )
        }

        if (!failReason) {
          return <span className='text-muted-foreground/60 text-xs'>-</span>
        }

        return (
          <>
            <button
              type='button'
              className='group flex max-w-[200px] items-center gap-1 text-left text-xs'
              onClick={() => setDialogOpen(true)}
              title={t('Click to view full error message')}
            >
              <span className='truncate leading-snug text-red-600 group-hover:underline dark:text-red-400'>
                {failReason}
              </span>
            </button>
            <FailReasonDialog
              failReason={failReason}
              open={dialogOpen}
              onOpenChange={setDialogOpen}
            />
          </>
        )
      },
      size: 200,
      maxSize: 220,
    }
  )

  return columns
}
